import React, { ReactNode, useEffect, useState } from 'react';
import { UserGroupOutlined } from '@duxton-web/icons';
import { Flex, Popover, Tag } from '@duxton-web/ui';
import { Timeline, Typography } from 'antd';
import styled from 'styled-components';

import { TIMESTAMP_FORMAT } from '.';
import { transformDateFromISO } from '../helper/utils';
import Service from '@/lib/App';
import { ApprovalFlowViewModel, Experiment } from '@/lib/PlannerDTOs';
import { defaultPresetColors } from '@/theme';
import { transformDate } from '@/utils/dateTime';

import './ExperimentDetailApprovalFlow.css';

import { GRABX_SLACK_CHANNEL, REQUESTED } from '@/constants';

const enum ApprovalFlowViewModelActionStr {
  PENDING = 'Pending Approval',
}

function Dot({ background }) {
  return (
    <div style={{ width: 10, height: 10, background, borderRadius: 100 }} />
  );
}

export default function ExperimentDetailApprovalFlow({
  experiment,
}: {
  experiment: Experiment;
}) {
  const [approvers, setApprovers] = useState({
    loading: false,
    users: [],
  });
  const [techFamilies, setTechFamilies] = useState<string[]>([]);

  async function fetchApprovers() {
    const variablesTechFamilies: string[] = [];
    const promises = [];

    if (experiment && experiment.variables)
      experiment.variables.forEach((variable) => {
        promises.push(Service.planner.fetchVariable(variable.name));
      });

    await Promise.all(promises).then((values) => {
      if (values) {
        values.forEach((value) => {
          if (!variablesTechFamilies.includes(value.techFamily))
            variablesTechFamilies.push(value.techFamily);
        });
      }
    });

    if (variablesTechFamilies) {
      try {
        const res = await Service.planner.getTechFamilyApprovers({
          tech_family: variablesTechFamilies,
        });

        if (res?.data?.users) {
          setApprovers({ loading: false, users: res?.data?.users });
          setTechFamilies(variablesTechFamilies);
        }
      } catch (e) {
        setApprovers({ loading: false, users: [] });
        setTechFamilies([]);
      }
    } else {
      setApprovers({ loading: false, users: [] });
      setTechFamilies([]);
    }

    return variablesTechFamilies;
  }

  useEffect(() => {
    if (experiment?.state === REQUESTED) {
      fetchApprovers();
    }
  }, []);

  function renderApprovers(d: ApprovalFlowViewModel): ReactNode {
    const { email, actionStr, resource } = d;

    if (
      actionStr !== ApprovalFlowViewModelActionStr.PENDING ||
      (email && resource)
    )
      return false;

    if (approvers.loading) return false;

    if (!approvers.users?.length)
      return (
        <Tag
          id="workflow-no-users"
          color="yellow"
          style={{
            width: 'fit-content',
          }}
        >
          No users able to approve this experiment. Please reach out to
          @oncall-experimentation-platform in{' '}
          <Typography.Link href={GRABX_SLACK_CHANNEL}>#grab-x</Typography.Link>
        </Tag>
      );

    const usersTag = approvers.users.map((user) => (
      <Tag
        color="white"
        key={user}
        style={{
          margin: '2px 0',
        }}
      >
        {user}
      </Tag>
    ));

    const usersTagContainer = (
      <Flex
        gap={4}
        wrap
        style={{
          maxWidth: 700,
          maxHeight: 300,
          overflow: 'hidden scroll',
        }}
      >
        {usersTag}
      </Flex>
    );

    return (
      <Popover content={usersTagContainer}>
        <Tag
          icon={<UserGroupOutlined />}
          color="white"
          style={{
            width: 'fit-content',
            cursor: 'pointer',
          }}
        >{`${techFamilies.join(',')} (${approvers.users.length} users)`}</Tag>
      </Popover>
    );
  }

  function renderFlow(d: ApprovalFlowViewModel): ReactNode {
    const { email, actionStr, resource } = d;

    let title: ReactNode = (
      <Flex gap={4}>
        <Typography.Text strong>{email}</Typography.Text>
        <Typography.Text>{actionStr.toLowerCase()}</Typography.Text>
      </Flex>
    );

    if (
      actionStr === ApprovalFlowViewModelActionStr.PENDING &&
      !email &&
      !resource
    ) {
      title = (
        <Typography.Text>
          Pending approval from the following tech family
        </Typography.Text>
      );
    }

    return (
      <Flex vertical gap={4}>
        {title}
        {resource && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Overlap with: {resource}
          </Typography.Text>
        )}
        {renderApprovers(d)}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {renderFlowDescription(d)}
        </Typography.Text>
      </Flex>
    );
  }

  function renderFlowDescription(d: ApprovalFlowViewModel): ReactNode {
    const { actionStr, timestamp, comments } = d;

    if (actionStr === ApprovalFlowViewModelActionStr.PENDING) {
      return `Experiment will be cancelled if not approved by ${transformDate(
        experiment?.startTime,
        true,
      )}`;
    }

    return `${transformDateFromISO(timestamp, TIMESTAMP_FORMAT)} - ${
      comments ? `Comment: ${comments}` : 'No Comment'
    }`;
  }

  let items = experiment.approvalFlow.data.map((d) => {
    return {
      dot: <Dot background={defaultPresetColors.green} />,
      color: '',
      children: renderFlow(d),
    };
  });
  items = [
    {
      dot: <Dot background={defaultPresetColors.green} />,
      color: '',
      children: (
        <Flex gap={4}>
          <Typography.Text strong>{experiment.owner}</Typography.Text>
          <Typography.Text>requested approval</Typography.Text>
        </Flex>
      ),
    },
    ...items,
  ];
  return (
    <BorderBox>
      <Timeline items={items} />
    </BorderBox>
  );
}

const BorderBox = styled.div`
  border: 1px solid #e8e8e8;
  padding: 16px 16px 0px;
  margin: 16px 32px 64px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: #ffffff;
  border: none;
  box-shadow: 0 4px 9px 0 rgb(22 26 29 / 5%);
`;
